import { Injectable } from '@angular/core';

import { environment } from '@environment';

/**
 * AmplitudeService — singleton analytics wrapper for the Brokers Portal.
 *
 * All interactions with the Amplitude Browser SDK go through this service.
 * In the local environment (no API key), the SDK is never initialized;
 * events are written to the browser console at debug level only.
 *
 * The SDK is loaded via dynamic import on first call to initialize(), keeping
 * it out of the initial bundle and reducing the application's initial load time.
 *
 * IMPORTANT: Callers must NOT pass raw user-input strings (email addresses,
 * phone numbers, free-text search queries, or form field contents) as event
 * properties. Only metadata (e.g., query length, field count) is acceptable.
 */
@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  /** True when running in local environment (no API key); SDK is not initialized. */
  private _isLocalMode = false;

  /**
   * Lazily-loaded Amplitude SDK module. Populated after initialize() resolves.
   * All methods guard against undefined to handle calls before initialization.
   */
  private _amplitude: typeof import('@amplitude/analytics-browser') | undefined = undefined;

  /**
   * Queue for track() events fired before the SDK finishes loading.
   * Flushed at the end of initialize() once _amplitude is ready.
   */
  private _pendingEvents: { eventName: string; properties?: Record<string, unknown> }[] = [];

  /**
   * Initialize the Amplitude SDK. Call once in AppComponent.ngOnInit().
   * The SDK is loaded asynchronously via dynamic import so it does not
   * contribute to the initial bundle. Events fired before initialize()
   * resolves are buffered in _pendingEvents and flushed immediately after
   * the SDK is ready — no events are lost.
   *
   * If the dynamic import fails (e.g., network error, ad-blocker), the service
   * falls into local mode to prevent unbounded _pendingEvents growth and queued
   * events are discarded silently.
   *
   * - If amplitudeApiKey is undefined (local environment), sets _isLocalMode=true
   *   and skips SDK initialization entirely (no network calls, no SDK side-effects).
   * - Otherwise loads the SDK via dynamic import (lazy — not in the initial bundle)
   *   and initializes it with environment-appropriate log level and manual page-view
   *   tracking (page_viewed events are dispatched by AppComponent).
   */
  async initialize(): Promise<void> {
    if (!environment.amplitudeApiKey) {
      this._isLocalMode = true;
      console.debug('[Amplitude] Local mode enabled — events will be logged to console only');
      return;
    }

    try {
      const amplitude = await import('@amplitude/analytics-browser');
      this._amplitude = amplitude;

      amplitude.init(environment.amplitudeApiKey, undefined, {
        logLevel: environment.production ? amplitude.Types.LogLevel.Warn : amplitude.Types.LogLevel.Debug,
        defaultTracking: {
          // Disable automatic page view tracking; AppComponent dispatches page_viewed manually
          pageViews: false,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
        },
      });

      // Flush events that were queued before the SDK finished loading
      for (const event of this._pendingEvents) {
        try {
          this._amplitude.track(event.eventName, event.properties);
        } catch {
          // Silent failure
        }
      }
    } catch {
      // SDK failed to load (e.g., network error, ad-blocker) — fall into local mode
      // so that subsequent track() calls are no-ops and _pendingEvents does not grow.
      this._isLocalMode = true;
      console.debug('[Amplitude] SDK failed to load — falling back to local mode');
    } finally {
      // Always clear the pending queue to release memory regardless of outcome
      this._pendingEvents = [];
    }
  }

  /**
   * Associate the authenticated broker's identity with all subsequent events.
   * Call immediately after OTP verification succeeds.
   *
   * @param agentId  Broker's agent UUID — used as the Amplitude user ID (non-PII)
   * @param companyId  Currently selected company UUID (non-PII)
   * @param firstName  Broker's first name (non-PII per spec)
   */
  setUserIdentity(agentId: string, companyId: string, firstName: string): void {
    try {
      if (this._isLocalMode) {
        console.debug('[Amplitude] setUserIdentity', { agentId, companyId, firstName });
        return;
      }
      if (!this._amplitude) {
        return;
      }
      this._amplitude.setUserId(agentId);
      const identifyEvent = new this._amplitude.Identify();
      identifyEvent.set('agent_id', agentId);
      identifyEvent.set('company_id', companyId);
      identifyEvent.set('first_name', firstName);
      this._amplitude.identify(identifyEvent);
    } catch {
      // Silent failure — analytics must never block the UI
    }
  }

  /**
   * Reset the Amplitude session and user identity.
   * Call on sign-out so the next login (possibly a different user on the same device)
   * starts a completely clean session.
   */
  resetIdentity(): void {
    try {
      if (this._isLocalMode) {
        console.debug('[Amplitude] resetIdentity');
        return;
      }
      if (!this._amplitude) {
        return;
      }
      this._amplitude.reset();
    } catch {
      // Silent failure
    }
  }

  /**
   * Track a named event with optional properties.
   *
   * If the SDK has not yet finished loading (i.e., initialize() has not resolved),
   * the event is queued and automatically flushed once the SDK is ready. This means
   * events fired during bootstrap (e.g., the first page_viewed) are not lost even
   * though the Amplitude SDK is loaded lazily via dynamic import.
   *
   * @param eventName  Snake_case event name from the Event Catalog (e.g., 'page_viewed')
   * @param properties  Optional event properties (must not contain PII or raw user input)
   */
  track(eventName: string, properties?: Record<string, unknown>): void {
    try {
      if (this._isLocalMode) {
        console.debug('[Amplitude]', eventName, properties);
        return;
      }
      if (!this._amplitude) {
        // SDK not yet loaded — queue the event for later dispatch
        this._pendingEvents.push({ eventName, properties });
        return;
      }
      this._amplitude.track(eventName, properties);
    } catch {
      // Silent failure — analytics must never block the UI
    }
  }

  /**
   * Update a single user property at runtime.
   * Use for properties that change independently of login (e.g., company_id after a switch).
   *
   * @param key    Property name (snake_case)
   * @param value  New value (must not be PII)
   */
  setUserProperty(key: string, value: string): void {
    try {
      if (this._isLocalMode) {
        console.debug('[Amplitude] setUserProperty', { key, value });
        return;
      }
      if (!this._amplitude) {
        return;
      }
      const identifyEvent = new this._amplitude.Identify();
      identifyEvent.set(key, value);
      this._amplitude.identify(identifyEvent);
    } catch {
      // Silent failure
    }
  }
}
